import axios from 'axios'
import type { components } from '@/types/generated'
import { bffBaseURL, normalizeError } from './apiClient'

// ============================================================================
// PDF Generation API — Phase 57 M9 + Phase 60 M10 + Phase 111c offer PDF
// ============================================================================

// Local type for the new BFF /pdf/offer endpoint. After regenerating
// generated.ts from the BFF OpenAPI, replace with the generated type.
export interface OfferPDFRequest {
  candidate_id: string
  job_id?: string
  hourly_rate?: number
  monthly_salary?: number
  currency: string
  start_date: string
  end_date?: string
  notes?: string
}

export async function generateOffer(offerData: OfferPDFRequest) {
  try {
    const response = await axios.post<Blob>(
      `${bffBaseURL}/api/v1/pdf/offer`,
      offerData,
      {
        responseType: 'blob',
        headers: {
          'Accept': 'application/pdf'
        }
      }
    )

    // Extract filename from Content-Disposition header or use default
    const contentDisposition = response.headers['content-disposition']
    let filename = 'oferta.pdf'
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/)
      if (filenameMatch && filenameMatch[1]) {
        filename = filenameMatch[1].replace(/['"]/g, '')
      }
    }

    // Create blob URL and trigger download
    const blob = new Blob([response.data], { type: 'application/pdf' })
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = filename
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(url)

    return { success: true, filename }
  } catch (error) {
    if (axios.isAxiosError(error)) {
      // Handle blob error responses
      if (error.response?.data instanceof Blob) {
        const text = await error.response.data.text()
        try {
          const json = JSON.parse(text)
          throw new Error(normalizeError(json.detail || 'Failed to generate offer'))
        } catch {
          throw new Error('Failed to generate offer letter')
        }
      }
      const message = normalizeError((error.response?.data as any)?.detail || 'Failed to generate offer')
      throw new Error(message)
    }
    throw error
  }
}

export async function generateCandidateCVPDF(
  request: components['schemas']['CVPDFRequest']
) {
  try {
    const response = await axios.post<Blob>(
      `${bffBaseURL}/api/v1/pdf/cv`,
      request,
      { responseType: 'blob' }
    )
    return response.data
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const message = normalizeError((error.response?.data as any)?.detail || 'No se pudo descargar el PDF del CV')
      throw new Error(message)
    }
    throw error
  }
}

export async function listPDFTemplates() {
  try {
    const response = await axios.get(
      `${bffBaseURL}/api/v1/pdf/templates`
    )
    return response.data
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const message = normalizeError((error.response?.data as any)?.detail || 'Failed to list templates')
      throw new Error(message)
    }
    throw error
  }
}
